// store/slices/superAdminAuthSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { superAdminAuthAPI } from "../../services/api";
import type {
  SuperAdminAuthState,
  SuperAdminLoginCredentials,
} from "../../types/superadmin";
import type { User } from "../../types";
import {
  beginDeliberateLogout,
  resetSessionNotices,
} from "../../utils/sessionEvents";

const initialState: SuperAdminAuthState = {
  superAdmin: getSuperAdminFromStorage(),
  token: localStorage.getItem("superAdminToken"),
  loading: false,
  error: null,
};

function getSuperAdminFromStorage(): User | null {
  const userStr = localStorage.getItem("superAdmin");
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as User;
  } catch {
    return null;
  }
}

// Login SuperAdmin
export const loginSuperAdmin = createAsyncThunk(
  "superAdminAuth/login",
  async (credentials: SuperAdminLoginCredentials, { rejectWithValue }) => {
    resetSessionNotices();
    try {
      const response = await superAdminAuthAPI.login(credentials);

      // Store in separate localStorage keys to avoid conflicts with tenant auth
      localStorage.setItem("superAdminToken", response.data.data.token);
      localStorage.setItem(
        "superAdmin",
        JSON.stringify(response.data.data.user),
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

// Logout SuperAdmin
export const logoutSuperAdmin = createAsyncThunk(
  "superAdminAuth/logout",
  async (_, { rejectWithValue }) => {
    // Same reason as the tenant logout: the server ends the session while this
    // tab still has requests in the air, and they come back as SESSION_REVOKED.
    beginDeliberateLogout();
    try {
      await superAdminAuthAPI.logout();
      localStorage.removeItem("superAdminToken");
      localStorage.removeItem("superAdmin");
      return true;
    } catch (error: any) {
      // Clear storage even if API call fails
      localStorage.removeItem("superAdminToken");
      localStorage.removeItem("superAdmin");
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  },
);

// Refresh Token
export const refreshSuperAdminToken = createAsyncThunk(
  "superAdminAuth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await superAdminAuthAPI.refreshToken();
      const { token } = response.data.data;
      localStorage.setItem("superAdminToken", token);
      return token;
    } catch (error: any) {
      localStorage.removeItem("superAdminToken");
      localStorage.removeItem("superAdmin");
      return rejectWithValue(
        error.response?.data?.message || "Session expired",
      );
    }
  },
);

// Get SuperAdmin Profile
export const getSuperAdminProfile = createAsyncThunk(
  "superAdminAuth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await superAdminAuthAPI.getProfile();
      localStorage.setItem(
        "superAdmin",
        JSON.stringify(response.data.data.user),
      );
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get profile",
      );
    }
  },
);

// Update SuperAdmin Profile
export const updateSuperAdminProfile = createAsyncThunk(
  "superAdminAuth/updateProfile",
  async (profileData: any, { rejectWithValue }) => {
    try {
      const response = await superAdminAuthAPI.updateProfile(profileData);
      localStorage.setItem(
        "superAdmin",
        JSON.stringify(response.data.data.user),
      );
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update profile",
      );
    }
  },
);

const superAdminAuthSlice = createSlice({
  name: "superAdminAuth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSuperAdminCredentials: (state, action) => {
      state.superAdmin = action.payload.user;
      state.token = action.payload.token;
    },
    clearSuperAdminCredentials: (state) => {
      state.superAdmin = null;
      state.token = null;
      localStorage.removeItem("superAdminToken");
      localStorage.removeItem("superAdmin");
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginSuperAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginSuperAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.superAdmin = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginSuperAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logoutSuperAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutSuperAdmin.fulfilled, (state) => {
        state.loading = false;
        state.superAdmin = null;
        state.token = null;
        state.error = null;
      })
      .addCase(logoutSuperAdmin.rejected, (state) => {
        state.loading = false;
        state.superAdmin = null;
        state.token = null;
      })

      // Refresh Token
      .addCase(refreshSuperAdminToken.fulfilled, (state, action) => {
        state.token = action.payload;
      })
      .addCase(refreshSuperAdminToken.rejected, (state) => {
        state.superAdmin = null;
        state.token = null;
      })

      // Get Profile
      .addCase(getSuperAdminProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSuperAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.superAdmin = action.payload;
        state.error = null;
      })
      .addCase(getSuperAdminProfile.rejected, (state) => {
        state.loading = false;
        state.superAdmin = null;
        state.token = null;
      })

      // Update Profile
      .addCase(updateSuperAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSuperAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.superAdmin = action.payload;
        state.error = null;
      })
      .addCase(updateSuperAdminProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSuperAdminCredentials,
  clearSuperAdminCredentials,
} = superAdminAuthSlice.actions;

export default superAdminAuthSlice.reducer;
